import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import shallowEqual from "shallowequal";
import { getCursorStopDataProps, getElementDataProps, getTextDataProps, } from "../dom-utils";
import { getElementMeta } from "../schema";
import { getItemLength, getUid, sliceMarkers } from "../utils";
import { EditorContext } from "./editor";
export const computeTextStyle = (format) => {
    var _a, _b;
    const textDecoration = [];
    if (format.underline) {
        textDecoration.push("underline");
    }
    if (format.overline) {
        textDecoration.push("overline");
    }
    if (format.dashed) {
        textDecoration.push("dashed");
    }
    if (format.lineThrough) {
        textDecoration.push("line-through");
    }
    return {
        fontWeight: format.bold ? "bold" : undefined,
        fontStyle: format.italic ? "italic" : undefined,
        textDecoration: textDecoration.length > 0 ? textDecoration.join(" ") : undefined,
        color: (_a = format.color) !== null && _a !== void 0 ? _a : undefined,
        backgroundColor: (_b = format.backgroundColor) !== null && _b !== void 0 ? _b : undefined,
    };
};
export const Text = ({ format, content, length, className, }) => {
    var _a, _b;
    const textDecoration = [];
    if (format.underline) {
        textDecoration.push("underline");
    }
    if (format.overline) {
        textDecoration.push("overline");
    }
    if (format.dashed) {
        textDecoration.push("dashed");
    }
    if (format.lineThrough) {
        textDecoration.push("line-through");
    }
    return (_jsx("span", Object.assign({ style: {
            fontWeight: format.bold ? "bold" : undefined,
            fontStyle: format.italic ? "italic" : undefined,
            textDecoration: textDecoration.length > 0 ? textDecoration.join(" ") : undefined,
            color: (_a = format.color) !== null && _a !== void 0 ? _a : undefined,
            backgroundColor: (_b = format.backgroundColor) !== null && _b !== void 0 ? _b : undefined,
        } }, getTextDataProps(length), { className: className }, { children: content }), void 0));
};
/**
 * 由于字体库按需加载里面没有u200b这个unicode，因此会导致CursorStop后面的字体渲染不出来(我不知道为啥浏览器这么笨)。
 * 2个解决方案：
 * 1.在@font-face里的unicode-range加上u200b（我不确定有没有什么隐患）
 * 2.在这里直接写死一个fontfamily，反正用户也看不到这个字符
 */
export const DEFAULT_FONT_FAMILY = "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica,'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol'";
const CursorStop = () => {
    return (_jsx("span", Object.assign({}, getCursorStopDataProps(), { style: {
            fontFamily: DEFAULT_FONT_FAMILY,
        } }, { children: "\u200b\u200b" }), void 0));
};
export const ContentNoMemo = ({ items, baseOffset, markers, path = [], previewOnly, boundary = true, }) => {
    const model = React.useContext(EditorContext);
    const itemWithOffsets = [];
    let offset = baseOffset;
    for (const item of items) {
        const endOffset = offset + getItemLength(item);
        itemWithOffsets.push({
            item,
            offset,
            endOffset,
        });
        offset = endOffset;
    }
    const renderItem = (item, offset, endOffset, path) => {
        switch (item.type) {
            case "text": {
                const Text = model.getTextComponent();
                return (_jsx(Text, { format: item.format, content: item.content, length: item.length, baseOffset: offset, markers: sliceMarkers(markers, offset, endOffset) }, getUid(item)));
            }
            case "element": {
                const ElementComponent = model.getElementComponent(item.tag);
                const meta = getElementMeta(item.tag);
                const contentStartOffset = offset + (meta.hasBoundary ? 1 : 0);
                const contentEndOffset = endOffset - (meta.hasBoundary ? 1 : 0);
                return (_jsxs(React.Fragment, { children: [!previewOnly && (meta.hasBoundary || !meta.hasContent) && (_jsx(CursorStop, {}, void 0)), _jsx(ElementComponent, Object.assign({ htmlDataProps: getElementDataProps(item) }, item.props, { baseOffset: offset, markers: sliceMarkers(markers, offset, endOffset), onChange: (data) => {
                                model.performChange((ctx) => {
                                    ctx.updateElementProps(path, data);
                                });
                            } }, { children: item.content && (_jsxs(_Fragment, { children: [!previewOnly && meta.hasBoundary && _jsx(CursorStop, {}, void 0), _jsx(Content, Object.assign({}, item.content, { baseOffset: contentStartOffset, markers: sliceMarkers(markers, contentStartOffset, contentEndOffset), path: path, previewOnly: previewOnly, boundary: meta.hasBoundary }), void 0), !previewOnly && meta.hasBoundary && _jsx(CursorStop, {}, void 0)] }, void 0)) }), void 0), !previewOnly && (meta.hasBoundary || !meta.hasContent) && (_jsx(CursorStop, {}, void 0))] }, getUid(item)));
            }
        }
    };
    return (_jsx(React.Fragment, { children: !previewOnly && itemWithOffsets.length === 0 && boundary ? (_jsx(CursorStop, {}, void 0)) : (itemWithOffsets.map(({ item, offset, endOffset }, index) => {
            const itemPath = path.slice(0);
            itemPath.push(index);
            return renderItem(item, offset, endOffset, itemPath);
        })) }, void 0));
};
const Content = React.memo(ContentNoMemo, (prevProps, nextProps) => {
    if (prevProps.items !== nextProps.items)
        return false;
    if (prevProps.length !== nextProps.length)
        return false;
    if (prevProps.baseOffset !== nextProps.baseOffset)
        return false;
    if (!shallowEqual(prevProps.path, nextProps.path))
        return false;
    if (!shallowEqual(prevProps.markers, nextProps.markers))
        return false;
    return true;
});
// TODO: markers equal
export default Content;
